// ============================================================
// SecaoTecnico.js — Painel do Técnico (Fase 4, trazida antes)
//
// Só leitura. Mostra, para a MODALIDADE do técnico logado, a frequência
// de cada atleta no mês escolhido: presenças, faltas, justificativas e
// classificação — usando a MESMA função de calculo.js do relatório e do
// dashboard (fonte única da verdade, nenhuma lógica de negócio duplicada).
//
// O técnico não cadastra, não edita, não gera Excel — só acompanha, pra
// poder cobrar os atletas com frequência baixa.
// ============================================================

import { useState, useEffect } from "react";
import { carregarDispensasDoMes, carregarNomesAtuais, carregarPresencasDoMes } from "../firebase";
import { calcularFrequencia, normalizarModalidade } from "../calculo";
import { MESES, CINZA_ESC, TEXTO, BRANCO, argbParaCss } from "../tema";
import { Card, Sel, StatusBox } from "../componentes";
import LegendaClassificacao from "../componentes/LegendaClassificacao";
import useIsMobile from "../useIsMobile";

export default function SecaoTecnico({ userNucleo, modalidade }) {
  const isMobile = useIsMobile();
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth();
  const [mes, setMes] = useState(String(mesAtual));
  const [ano, setAno] = useState(String(anoAtual));
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [resultado, setResultado] = useState(null); // { totalTreinos, membros }
  const [dispensasModalidade, setDispensasModalidade] = useState([]);

  const anos = [anoAtual - 1, anoAtual, anoAtual + 1].map(String);
  const modalidadeNorm = normalizarModalidade(modalidade);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      try {
        const mesNum = parseInt(mes);
        const anoNum = parseInt(ano);

        // O banco filtra o mês, e não o navegador: antes vinham 2.499
        // documentos para mostrar os 434 do mês.
        const registrosDoMes = await carregarPresencasDoMes(userNucleo, mesNum, anoNum);

        const dispensasDoMes = await carregarDispensasDoMes(userNucleo, mesNum, anoNum);
        const nomesAtuais = await carregarNomesAtuais(userNucleo);

        const calc = calcularFrequencia({ registros: registrosDoMes, dispensas: dispensasDoMes, mes: mesNum, ano: anoNum, nomesAtuais });
        // Só os atletas da própria modalidade — pior frequência primeiro,
        // pra facilitar cobrar quem está devendo.
        const meusAtletas = calc.membros
          .filter(m => m.grupo === "ATLETA" && m.modalidade === modalidadeNorm)
          .sort((a, b) => a.pctTotal - b.pctTotal);

        // Dispensas que afetam a modalidade do técnico: coletivas (afetam
        // todo mundo) + as que foram cadastradas especificamente pra essa
        // modalidade. Assim ele sabe quais dias não contam sem perguntar.
        const relevantes = dispensasDoMes
          .filter(d => d.tipo === "COLETIVA" || (d.tipo === "MODALIDADE" && normalizarModalidade(d.modalidade) === modalidadeNorm))
          .sort((a, b) => a.data.localeCompare(b.data));

        if (ativo) {
          setResultado({ totalTreinos: calc.totalTreinos, membros: meusAtletas });
          setDispensasModalidade(relevantes);
        }
      } catch (e) {
        if (ativo) setErro("Erro ao carregar dados: " + (e.message || ""));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => { ativo = false; };
  }, [mes, ano, userNucleo, modalidadeNorm]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
          Meus atletas · {modalidadeNorm}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <Sel label="Mês" value={mes} onChange={setMes}
            options={MESES.map((m, i) => ({ value: String(i), label: m }))} />
          <Sel label="Ano" value={ano} onChange={setAno} options={anos} />
        </div>
      </Card>

      {erro && <StatusBox tipo="erro" msg={erro} />}

      <Card>
        {carregando ? (
          <div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando...</div>
        ) : !resultado || resultado.membros.length === 0 ? (
          <div style={{ fontSize: 13, color: CINZA_ESC }}>
            Nenhum atleta de {modalidadeNorm} com registros neste mês.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 4 }}>
              {/* O dia de hoje só entra na contagem depois das 18h30, quando o
                  treino fecha — antes disso ele aparece na grade como coluna
                  "aguardando" mas não conta. Sem dizer isso, a tela mostra
                  N dias ao lado de uma grade com N+1 colunas e parece erro. */}
              {resultado.totalTreinos} dias de treino no mês
              {resultado.diaHojeAguardando ? " (hoje ainda em andamento)" : ""}
              {" · "}{resultado.membros.length} atleta{resultado.membros.length !== 1 ? "s" : ""}
            </div>
            {resultado.membros.map(m => (
              <div key={m.nome} style={{ border: `1.5px solid ${argbParaCss(m.classif.cor)}55`, background: `${argbParaCss(m.classif.bg)}`, borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: TEXTO }}>{m.nome}</div>
                  <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 2 }}>
                    Presenças: {m.presEfetiva} · Faltas justificadas: {m.jc + m.jm + m.ji} · Faltas não justificadas: {m.faltas}
                  </div>
                </div>
                <div style={{ textAlign: "center", minWidth: 90 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: argbParaCss(m.classif.cor) }}>
                    {Math.round(m.pctTotal * 100)}%
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: BRANCO, color: argbParaCss(m.classif.cor), marginTop: 2, whiteSpace: "nowrap" }}>
                    {m.classif.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {dispensasModalidade.length > 0 && (
        <Card>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
            Datas especiais do mês
          </div>
          <div style={{ fontSize: 12, color: CINZA_ESC, marginBottom: 12 }}>
            Como cada data afeta a frequência: <b>Sem treino</b> (feriado ou suspensão coletiva, o dia não conta),{" "}
            <b>Falta justificada</b> (dispensa da modalidade, abona a ausência), e{" "}
            <b>Conta como presença</b> (competição, o atleta pontua presença direto).
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {dispensasModalidade.map(d => {
              const ehCompeticao = d.tipo === "MODALIDADE" && (d.subtipo || "").toLowerCase().includes("competi");
              const badge = d.tipo === "COLETIVA"
                ? { texto: "Sem treino", bg: "#EDE9FE", cor: "#7C3AED" }
                : ehCompeticao
                  ? { texto: "Conta como presença", bg: "#D1FAE5", cor: "#047857" }
                  : { texto: "Falta justificada", bg: "#FEF3C7", cor: "#92400E" };
              return (
                <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, padding: "6px 0", borderBottom: "1px solid #F1F5F9", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: TEXTO, minWidth: 48 }}>
                    {new Date(d.data + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: badge.bg, color: badge.cor, whiteSpace: "nowrap" }}>
                    {badge.texto}
                  </span>
                  <span style={{ color: CINZA_ESC }}>{d.subtipo}{d.motivo ? ` — ${d.motivo}` : ""}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card>
        <LegendaClassificacao />
      </Card>
    </div>
  );
}
